import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { useToast } from '../hooks/useToast';
import { api } from '../lib/api';
import { GlassCard } from '../components/GlassCard';
import { ArrowLeft, Phone, Send, Eye, CheckSquare, Square } from 'lucide-react';

const BUCKET_STYLES = {
  '0-30': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  '31-60': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  '61-90': 'bg-orange-50 text-orange-700 border-orange-200',
  '90+': 'bg-red-50 text-red-700 border-red-200',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function OutstandingDetail() {
  const { id, key } = useParams();
  const partyId = id || key;
  const isReceivable = !!id;
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [whatsappLoading, setWhatsappLoading] = useState(null);

  const load = useCallback(async () => {
    if (!token || !partyId) return;
    setLoading(true);
    try {
      const fn = isReceivable ? api.outstandingReceivableDetail : api.outstandingPayableDetail;
      const r = await fn(token, partyId);
      setData(r);
    } catch (e) {
      if (e.status !== 401) toast.show(e.message || 'Failed to load', 'error');
    } finally { setLoading(false); }
  }, [token, partyId, isReceivable, toast]);

  useEffect(() => { load(); }, [load]);

  const party = isReceivable ? data?.customer : data?.vendor;
  const partyName = party?.customer_name || party?.vendor_name || 'Unknown';
  const partyPhone = party?.customer_phone || party?.vendor_phone;
  const invoices = data?.invoices || [];

  const toggleSelect = (invId) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(invId)) n.delete(invId); else n.add(invId);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === invoices.length) setSelected(new Set());
    else setSelected(new Set(invoices.map(i => i.invoice_id)));
  };

  const sendWhatsapp = async (invoiceIds) => {
    const key2 = invoiceIds.length === 1 ? invoiceIds[0] : 'bulk';
    setWhatsappLoading(key2);
    try {
      const payload = invoiceIds.length === 1
        ? { invoice_id: invoiceIds[0] }
        : { invoices: invoiceIds };
      const r = await api.outstandingWhatsappPreview(token, payload);
      if (r.whatsapp_url) {
        window.open(r.whatsapp_url, '_blank');
        toast.show('WhatsApp opened in new tab', 'success');
      } else {
        toast.show('No WhatsApp URL returned', 'error');
      }
    } catch (e) {
      toast.show(e.message || 'Failed to generate reminder', 'error');
    } finally { setWhatsappLoading(null); }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="outstanding-detail-page">
        <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="outstanding-detail-page">
      <Link to="/outstanding" className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-semibold hover:text-gray-700 mb-4" data-testid="outstanding-back">
        <ArrowLeft size={14} /> Back to Outstanding
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-gray-900 font-extrabold text-xl" data-testid="party-name">{partyName}</h1>
          {partyPhone && (
            <a href={`tel:${partyPhone}`} className="inline-flex items-center gap-1.5 text-gray-500 text-sm hover:text-brand-purple transition-colors mt-1" data-testid="party-phone">
              <Phone size={12} /> {partyPhone}
            </a>
          )}
          {party?.state && <p className="text-gray-400 text-xs mt-0.5">{party.state}</p>}
        </div>
        <div className="text-right">
          <p className="text-gray-900 font-extrabold text-2xl tabular-nums" data-testid="party-total">{formatMoney(data?.total_outstanding || 0)}</p>
          <p className="text-gray-400 text-xs mt-0.5">{invoices.length} unpaid invoice{invoices.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Bulk actions */}
      {isReceivable && selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
          <span className="text-amber-700 text-sm font-bold">{selected.size} selected</span>
          <button onClick={() => sendWhatsapp([...selected])}
            disabled={whatsappLoading === 'bulk'}
            data-testid="bulk-whatsapp-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50">
            <Send size={14} /> {whatsappLoading === 'bulk' ? 'Generating...' : 'Send Bulk Reminder'}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-gray-500 text-xs font-semibold hover:text-gray-700 ml-auto">Clear</button>
        </div>
      )}

      {/* Table */}
      <GlassCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="outstanding-invoices-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {isReceivable && (
                  <th className="px-4 py-3 w-10">
                    <button onClick={toggleAll} className="text-gray-400 hover:text-gray-700" data-testid="select-all-btn">
                      {selected.size === invoices.length && invoices.length > 0
                        ? <CheckSquare size={16} className="text-amber-600" />
                        : <Square size={16} />}
                    </button>
                  </th>
                )}
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Invoice #</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Date</th>
                <th className="text-right px-4 py-3 text-gray-500 text-xs font-bold uppercase">Total</th>
                <th className="text-right px-4 py-3 text-gray-500 text-xs font-bold uppercase">Paid</th>
                <th className="text-right px-4 py-3 text-gray-500 text-xs font-bold uppercase">Amount Due</th>
                <th className="text-right px-4 py-3 text-gray-500 text-xs font-bold uppercase">Days</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Bucket</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Status</th>
                <th className="text-right px-4 py-3 text-gray-500 text-xs font-bold uppercase w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={isReceivable ? 10 : 9} className="py-16 text-center">
                    <p className="text-2xl mb-2">🎉</p>
                    <p className="text-gray-500 text-sm">All invoices are paid!</p>
                  </td>
                </tr>
              ) : invoices.map(inv => (
                <tr key={inv.invoice_id} data-testid={`inv-row-${inv.invoice_id}`}
                  className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                  {isReceivable && (
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSelect(inv.invoice_id)} className="text-gray-400 hover:text-gray-700" data-testid={`select-${inv.invoice_id}`}>
                        {selected.has(inv.invoice_id)
                          ? <CheckSquare size={16} className="text-amber-600" />
                          : <Square size={16} />}
                      </button>
                    </td>
                  )}
                  <td className="px-4 py-3 text-gray-900 font-bold">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDate(inv.sale_date)}</td>
                  <td className="px-4 py-3 text-right text-gray-500 tabular-nums">{formatMoney(inv.total)}</td>
                  <td className="px-4 py-3 text-right text-emerald-600 tabular-nums font-semibold">{inv.partial_paid_amount > 0 ? formatMoney(inv.partial_paid_amount) : '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(inv.amount_due)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold" style={{ color: inv.days_overdue > 90 ? '#DC2626' : inv.days_overdue > 60 ? '#EA580C' : inv.days_overdue > 30 ? '#CA8A04' : '#059669' }}>
                    {inv.days_overdue}d
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${BUCKET_STYLES[inv.bucket] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      {inv.bucket}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${inv.payment_status === 'partial' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : inv.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                      {inv.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isReceivable && (
                        <button onClick={() => sendWhatsapp([inv.invoice_id])}
                          disabled={whatsappLoading === inv.invoice_id}
                          data-testid={`wa-btn-${inv.invoice_id}`}
                          className="p-2 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
                          title="Send WhatsApp Reminder">
                          <Send size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}
